import React, { useState, useEffect } from 'react';
import GamifiedButton from '../common/GamifiedButton';
import { Confetti } from '../effects';
import { useLang } from '../../i18n/LanguageContext';

export default function HeroSection({ onDiscoverClick }) {
  const [scrollPos, setScrollPos] = useState(0);
  const { t } = useLang();

  useEffect(() => {
    const handleScroll = () => {
      setScrollPos(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#F7F4EC] via-[#F3F2EA] to-[#FAE8DC] pt-20 pb-32 px-5 md:py-10">

      {/* Confetti Animation */}
      <Confetti count={25} speed="slow" />

      {/* Background Pattern - Enhanced with more shapes */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <div className="absolute w-[300px] h-[300px] bg-[#2F7E72] rounded-full opacity-[0.08] top-[10%] left-[5%] animate-float-continuous"></div>
        <div className="absolute w-[200px] h-[200px] bg-[#A8D5BA] rounded-full opacity-[0.08] top-[60%] right-[10%] animate-float-continuous stagger-2"></div>
        <div className="absolute w-[150px] h-[150px] bg-[#F4A6C1] rounded-full opacity-[0.08] bottom-[20%] left-[20%] animate-float-continuous stagger-4"></div>
        {/* Extra decorative hearts */}
        <div className="absolute text-4xl top-[15%] right-[15%] animate-heartbeat opacity-40">💕</div>
        <div className="absolute text-3xl bottom-[30%] right-[25%] animate-heartbeat stagger-3 opacity-30">✨</div>
      </div>

      {/* Container */}
      <div className="relative z-10 max-w-[1200px] w-full grid grid-cols-1 md:grid-cols-2 gap-15 items-center">

        {/* Content */}
        <div className="flex flex-col gap-6 md:animate-[fadeInUp_1s_ease-out]">

          {/* Badge */}
          <span className="inline-flex items-center gap-2 bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] px-5 py-3 rounded-2xl text-[13px] font-semibold uppercase tracking-wide w-fit md:animate-[fadeInUp_1s_ease-out_0.1s_backwards]">
            {t('hero.badge')}
          </span>

          {/* Titre — les tailles vont du plus petit au plus grand : Tailwind est
              mobile-first, l'ancien « text-[56px] sm:text-[36px] » affichait donc
              56 px sur un téléphone et 36 px sur un écran d'ordinateur. */}
          <h1 className="m-0 text-[32px] sm:text-[40px] md:text-[56px] font-black leading-tight text-[#33403B] uppercase tracking-tight md:animate-[fadeInUp_1s_ease-out_0.2s_backwards]">
            <span className="block md:animate-[slideIn_0.8s_cubic-bezier(0.25,0.46,0.45,0.94)]">{t('hero.titre.ligne1')}</span>
            <span className="block bg-gradient-to-br from-[#4AA294] to-[#2F7E72] bg-clip-text text-transparent md:animate-[slideIn_0.8s_cubic-bezier(0.25,0.46,0.45,0.94)]">
              {t('hero.titre.ligne2')}
            </span>
          </h1>

          {/* Description */}
          {/* Le contresens le plus coûteux du site : un visiteur qui ne connaît
              pas le concept croit que le jeu va LUI apprendre le sexe du bébé.
              La première phrase lève ce malentendu, la seconde dit qu'on achète
              un objet physique — rien ne le disait avant le deuxième écran. */}
          <p className="text-lg leading-relaxed text-[#33403B] m-0 max-w-[550px] animate-[fadeInUp_1s_ease-out_0.3s_backwards]">
            <strong>{t('hero.accroche.fort')}</strong>
            <br />
            {t('hero.accroche.texte')}
            <br />
            <span className="mt-3 inline-block font-semibold text-[#2F7E72]">
              {t('hero.accroche.objet')}
            </span>
          </p>

          {/* CTA */}
          <div className="flex gap-4 flex-wrap mt-4 animate-[fadeInUp_1s_ease-out_0.4s_backwards]">
            <GamifiedButton
              variant="primary"
              size="lg"
              onClick={onDiscoverClick}
              className="min-w-[200px]"
              data-cta="home:hero-decouvrez"
            >
              {t('hero.cta.offre')}
            </GamifiedButton>
            <GamifiedButton
              variant="secondary"
              size="lg"
              onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
              className="min-w-[200px]"
            >
              {t('hero.cta.enSavoirPlus')}
            </GamifiedButton>
          </div>

          {/* Features - with bouncy hover */}
          <div className="grid grid-cols-3 gap-5 mt-6 pt-6 border-t-2 border-[#2F7E72]/20 animate-[fadeInUp_1s_ease-out_0.5s_backwards]">
            {[
              { icon: '🧩', text: t('hero.atout.pieces') },
              { icon: '👨‍👩‍👧‍👦', text: t('hero.atout.joueurs') },
              { icon: '⏱️', text: t('hero.atout.duree') },
            ].map((feature, i) => (
              <div key={i} className="flex flex-col items-center gap-3 text-center group cursor-default">
                <span
                  className="text-[32px] inline-block animate-bouncy transition-transform duration-300 group-hover:scale-110"
                  style={{ animationDelay: `${i * 0.2}s` }}
                >
                  {feature.icon}
                </span>
                <span className="text-[13px] text-[#33403B] font-semibold uppercase tracking-wide group-hover:text-[#4AA294] transition-colors">
                  {feature.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Image */}
        <div className="relative flex items-center justify-center animate-[fadeIn_1s_ease-out_0.3s_backwards] order-first md:order-last">
          <img
            src="/puzzle-ours-1024.webp"
            srcSet="/puzzle-ours-600.webp 600w, /puzzle-ours-1024.webp 1024w"
            sizes="(max-width: 768px) 90vw, 480px"
            alt={t('hero.imageAlt')}
            className="max-w-full max-h-[70vh] h-auto w-auto rounded-3xl shadow-[0_24px_48px_rgba(47,126,114,0.22)] animate-[float_3s_ease-in-out_infinite]"
            width="600"
            height="900"
            style={{ transform: `translateY(${scrollPos * 0.3}px)` }}
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute w-[112%] h-[108%] bg-gradient-to-br from-[#4AA294]/12 to-[#EDB82F]/12 rounded-[2rem] -z-10 border-3 border-dashed border-[#4AA294]/60"></div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-[30px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#2F7E72] text-xs animate-[fadeInUp_1s_ease-out_0.6s_backwards]">
        <div className="w-6 h-10 border-2 border-[#2F7E72] rounded-xl relative">
          <div className="absolute top-2 left-1/2 w-1 h-2 bg-[#2F7E72] rounded-sm -translate-x-1/2 animate-[scrollBounce_1.5s_ease-in-out_infinite]"></div>
        </div>
        <span>{t('hero.scroll')}</span>
      </div>

      {/* Keyframes in style tag */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes bounceSoft {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes scrollBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(8px); }
        }
      `}</style>
    </div>
  );
}
